import { cookies } from 'next/headers';
import type { InsforgeUser } from '@insforge/shared';
import { COOKIE_NAMES, parseUserCookie } from '../lib/cookies';

/**
 * Session information extracted from cookies
 */
export interface InsforgeAuth {
  /**
   * The current user's ID, or null if not authenticated
   */
  userId: string | null;

  /**
   * The current session token, or null if not authenticated
   */
  token: string | null;

  /**
   * User information (email, name) if available
   */
  user: InsforgeUser | null;
}

/**
 * Get authentication information from cookies in Server Components,
 * Route Handlers, and Server Actions.
 *
 * This function reads the HTTP-only cookies set by the middleware
 * and returns user authentication data.
 *
 * @example
 * ```ts
 * // In a Server Component
 * import { auth } from '@insforge/nextjs/server';
 *
 * export default async function Page() {
 *   const { userId, token } = await auth();
 *
 *   if (!userId) {
 *     return <div>Not authenticated</div>;
 *   }
 *
 *   return <div>User ID: {userId}</div>;
 * }
 * ```
 *
 * @example
 * ```ts
 * // In an API Route Handler
 * import { auth } from '@insforge/nextjs/server';
 * import { createClient } from '@insforge/sdk';
 *
 * export async function GET() {
 *   const { userId, token } = await auth();
 *
 *   if (!userId || !token) {
 *     return Response.json({ error: 'Unauthorized' }, { status: 401 });
 *   }
 *
 *   // Use token with SDK
 *   const insforge = createClient({
 *     baseUrl: process.env.INSFORGE_BASE_URL!,
 *     edgeFunctionToken: token,
 *   });
 *
 *   const result = await insforge.database.from('posts').select();
 *   return Response.json(result.data);
 * }
 * ```
 *
 * @example
 * ```ts
 * // In a Server Action
 * 'use server';
 *
 * import { auth } from '@insforge/nextjs/server';
 *
 * export async function createPost(formData: FormData) {
 *   const { userId } = await auth();
 *
 *   if (!userId) {
 *     throw new Error('Not authenticated');
 *   }
 *
 *   // Create post with userId
 * }
 * ```
 */
export async function auth(): Promise<InsforgeAuth> {
  const cookieStore = await cookies();

  // Get session token
  const token = cookieStore.get(COOKIE_NAMES.SESSION)?.value || null;

  // Get user info using centralized parsing
  const userCookie = cookieStore.get(COOKIE_NAMES.USER)?.value;
  const user = parseUserCookie(userCookie);
  const userId = user?.id || null;

  return {
    userId,
    token,
    user,
  };
}
